import aCallsB from './a-calls-b.png'
import antiCorruptionLayer from './anti-corruption-layer.png'
import indirection from './indirection.png'
import events from './events.png'
import inversionOfDependencies from './inversion-of-dependencies.png'

const content = `<p>The principle of “Protected variation” consists in making the interactions between components such that the changes in one component don’t affect the other. This reduces coupling between two components.</p>

<p>What do we mean by “components”? By component we mean group of classes and functions that gravitate towards a common theme. Such themes can be: loading data, displaying a view, performing a complex calculation, handling the interaction with an external service, encrypting and decrypting data, and so on.</p>

<p>Let’s call our two components A and B, and say that some code in A calls some code defined in B.</p>

<img src=${aCallsB} alt="a-calls-b" />

<p>By default, B is protected from the variations in A: it is A that uses B, so if the code of A changes there is little reason (at least in theory, if there is no <a href="/encapsulation">leaky abstraction</a>) for the code of B to change. B only provides a service.</p>

<p>But what if we want the opposite, to protect A from the variations of B? If the code of B changes, how can we make it so that A doesn’t have to be changed in turn?</p>

<p>This is where the techniques of Protected variation comes into play.</p>

<p>Here is an abstract overview on several ways to proceed. The purpose is to show that it’s possible to be protected from a code that we call, and that there are various approaches to do it.</p>

<p>We’ll get to the concrete details of these approaches in dedicated articles.</p>

<h2 id="Reducing-the-surface-of-exposure">Reducing the surface of exposure</h2>

<p>One way to protect most of the code of A is to ensure that all the direct calls to B are grouped in one specific location in A:</p>

<img src=${antiCorruptionLayer} alt="anti-corruption-layer" />

<p>In the terminology of Domain Driven Design, this is called an <a href="/DDD">Anti-Corruption Layer</a>.</p>

<h2 id="Indirection">Indirection</h2>

<p>Another way is to introduce another component between A and B. If B changes, this intermediary component can adapt to the change instead of A.</p>

<img src=${indirection} alt="indirection" />

<p>This is called an Indirection. Some types of indirections like <a href="/adapter">Adapter</a> resemble an Anti-Corruption Layer that was taken outside of A.</p>

<h2 id="Observation">Observation</h2>

<p>Instead of a direct call, A can publish a piece of information that it doesn’t know who will receive. B subscribes to be notified when this information is published and reacts accordingly. A no longer knows about B, it’s B that knows about the events that A can publish:</p>

<img src=${events} alt="events" />

<p>This type of information is called an <a href="/events">Event</a>, and this concept is close to <a href="/observer">Observer</a> pattern.</p>

<h2 id="Inversion-of-dependencies">Inversion of dependencies</h2>

<p>Finally, A can define an interface consisting of a set of functions that it needs to call. B implements this interface by providing implementations for those functions:</p>

<img src=${inversionOfDependencies} alt="inversion-of-dependencies" />

<p>When B changes, it continues to implement those functions with its new code, and A and its interface don’t change.</p>

<h2 id="When-is-it-useful-to-protect-a-piece-of-code?">When is it useful to protect a piece of code?</h2>

<p>The above techniques are efficient to protect a piece of code from another, but they come with their own overhead. We wouldn’t apply them to protect any random piece of code from another. But there are several case where they’re worth their overhead.</p>

<p>One case is when we want to prevent concepts of a large subpart of the codebase to spread into another. A typical example is to maintain DDD’s <a href="/DDD">bounded contexts</a>.</p>

<p>Another case is to avoid circular dependencies. If B already uses A, we don’t want A to also know about B. Otherwise neither one could be understood on its own and that creates a strong coupling between them.</p>

<p>A third case is to have dependencies in the code that makes sense from a product perspective. If it doesn’t make sense from a functional point of view that A knows about B, for example when A has use cases that don’t involve B, then we’d want to protect A from B.</p>

<p>An example to illustrate this is the support requests and conversations case we see in <a href="/high-cohesion">High cohesion</a>: conversations can exist without support requests, so it should be the code of support requests that knows about conversations and not the other way round.</p>

<p>A last example is to protect domain code from technical code. This is the core idea of <a href="/hexagonal-architecture">Hexagonal architecture</a>.</p>

<h2 id="Any-piece-of-code-can-be-protected">Any piece of code can be protected</h2>

<p>There are many ways to protect callers from callees and decide which code depends on which. This gives as many tools to decide what parts of the code should be protected from the changes in others.</p>

<p>We’ll get to the details of why and how to achieve this in detail.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Callees are naturally protected from the changes in callers</li><li>There are techniques to also protect callers from callees when necessary</li></ul>
</div>`

export default content
